import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";

export const useLiveStore = defineStore(
    "live",
    () => {
        const selectedHours = ref<number>(24);
        // The live release filter (`stek_release` the dashboards scope to, or
        // null for "all releases"). The picker that let a user choose one was
        // removed, so this stays null — every live query is unscoped. The field
        // is kept because the dashboards still thread it through; re-adding a
        // picker only needs a control that writes it.
        const selectedRelease = ref<string | null>(null);
        // Absolute custom window (ISO strings, hour granularity). When both are
        // set they override selectedHours for every live query.
        const customStart = ref<string | null>(null);
        const customEnd = ref<string | null>(null);
        // Epoch ms when the live data last loaded, so the page can show
        // "updated Xs ago". Runtime only — never persisted (a restored stale
        // timestamp would read as fresh).
        const lastUpdatedAt = ref<number | null>(null);
        // Events-per-bucket for the title-row sparkline (the "heartbeat"), set by
        // the metrics dashboard on each load. Runtime only.
        const sparklinePoints = ref<number[]>([]);
        // Headline KPI counts (Completed / Failed / Active) for the title-row
        // pulse, set alongside the sparkline. Runtime only.
        const pulseStats = ref<{
            done: number;
            failed: number;
            active: number;
        } | null>(null);

        /** Clear per-stek release state when switching steks. */
        const resetForNewStek = () => {
            selectedRelease.value = null;
        };

        return {
            selectedHours,
            customStart,
            customEnd,
            selectedRelease,
            lastUpdatedAt,
            sparklinePoints,
            pulseStats,
            resetForNewStek,
        };
    },
    {
        persist: {
            storage: localStorage,
            pick: ["selectedHours", "customStart", "customEnd"],
        },
    }
);

export const useLiveManager = () => storeToRefs(useLiveStore());

/**
 * Request-body fragment for the current time window: an absolute range when a
 * custom start/end is set, otherwise the relative last-N-hours.
 */
export function liveTimeBody():
    { hours: number } | { start_time: string; end_time: string } {
    const store = useLiveStore();
    if (store.customStart && store.customEnd) {
        return { start_time: store.customStart, end_time: store.customEnd };
    }
    return { hours: store.selectedHours };
}

/** Effective window length in hours — drives chart label granularity. */
export function liveWindowHours(): number {
    const store = useLiveStore();
    if (store.customStart && store.customEnd) {
        return Math.max(
            1,
            (new Date(store.customEnd).getTime() -
                new Date(store.customStart).getTime()) /
                3_600_000
        );
    }
    return store.selectedHours;
}
